import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { authApi } from '@/api/auth'
import { getApiErrorMessage } from '@/utils/apiError'

interface Props {
  isOpen: boolean
  currentName: string
  onClose: () => void
  onSaved: (newName: string) => void
}

export function NameEditModal({ isOpen, currentName, onClose, onSaved }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const [name, setName] = useState(currentName)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setName(currentName)
      setError(null)
    }
  }, [isOpen, currentName])

  const mutation = useMutation({
    mutationFn: async (fullName: string) => {
      const res = await authApi.updateProfile({ fullName })
      return res.data.data
    },
    onSuccess: () => {
      toast.success(t('admin:profile.name.toastSaved'))
      onSaved(name.trim())
      onClose()
    },
    onError: (err) => {
      setError(getApiErrorMessage(err, t))
    },
  })

  const handleSave = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('admin:profile.name.required'))
      return
    }
    setError(null)
    mutation.mutate(trimmed)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin:profile.name.modalTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor="lc-edit-name" className="block text-sm font-medium text-gray-700">
            {t('admin:profile.name.label')}
          </label>
          <input
            id="lc-edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            placeholder={t('admin:profile.name.placeholder')}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common:button.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin:profile.name.saveButton')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
